import type { BusinessTerm } from '@wix/ambassador-funnel-taxonomy-v1-business-term/types'
import {
  mainFunnelIntroCategoryUserTyping,
  mainFunnelIntroClickAnAnswer,
  mainFunnelIntroGeneralAction,
  mainFunnelIntroMultipleAnswers,
  mainFunnelIntroScreenView,
  topFunnelAppsSelected,
  topFunnelFinishFlow,
  topFunnelStartFlow,
} from '@wix/bi-logger-mfi/v2'
import { postRegPageClickOnGo } from '@wix/bi-logger-spettatore-bi/v2'
import { factory } from '@wix/web-bi-logger'

export type CreateFlowLoggerParams = {
  flowBiSlug: string
  metaSiteId?: string
}

type AppsSelectedParams = {
  app_id: string
  built_in_app_id: string
}

type CreatePageLoggerParams = {
  pageBiSlug: string
  questionName?: string
}

type ScreenViewParams = {
  initialData?: string | object
  questionName?: string
}

type ClickAnswerParams = {
  questionKey: string
  buttonName: string
  suggestedOption?: boolean
  position?: number
}

type AnswerCategoryParams = {
  businessTerm?: BusinessTerm
  segment?: string
  businessSegmentId?: string
  userSearchInput?: string
}

type TypingParams = {
  text: string
  returnedResults: number
}

type StartFlowParams = {
  referralInfo?: string
  referralUrl?: string
}

type AnswerParams = {
  answer: string | object
  payload?: object
  position?: number
  error?: string
  questionName?: string
  additionalInfo?: string
}

type GeneralActionParams = {
  action: string
  questionKey?: string
  error?: string
}

type BackParams = {
  targetBiSlug?: string
  payload?: object
  questionName?: string
}

type FinishFlowParams = {
  targetUrl: string
  screenName: string
  screenVersion: string
}

type SkipParams = {
  payload?: object
}

export const createFlowLogger = (flowParams: CreateFlowLoggerParams) => {
  const { flowBiSlug, metaSiteId } = flowParams

  const logger = factory().logger().updateDefaults({
    _msid: metaSiteId,
  })

  type ReportProps = Parameters<typeof logger.report>[0]
  type ReportResult = ReturnType<typeof logger.report>

  const withReport = <Args extends any[]>(fn: (...args: Args) => ReportProps): ((...args: Args) => ReportResult) => {
    return (...args) => logger.report(fn(...args))
  }

  const createPageLogger = (pageParams: CreatePageLoggerParams) => {
    const { pageBiSlug, questionName } = pageParams

    const [screenName, screenVersion] = parsePageBiSlug(pageBiSlug)

    const common = {
      flow: flowBiSlug,
      screenName,
      question_name: questionName,
      screen_version: screenVersion,
    }

    return {
      /** 2:30 */
      answerCategory: withReport(
        ({ businessTerm, segment, businessSegmentId, userSearchInput }: AnswerCategoryParams) => {
          const {
            postRegVertical,
            displayName,
            industryId,
            structureId,
            segmentationIndustryId,
            businessType,
            businessTermId,
          } = businessTerm || {}
          // this is a different endpoint logger, but it feels like that this is the place for it to be
          return postRegPageClickOnGo({
            ...common,
            cat: postRegVertical || '',
            subcat: displayName,
            sourceScreen: 'new/intro',
            segment: segment || '',
            industryId: industryId || '',
            structureId: structureId || '',
            subIndustry: segmentationIndustryId || '',
            businessType: businessType || '',
            business_term_id: businessTermId,
            businessSegment: businessSegmentId,
            free_text: userSearchInput || '',
          })
        },
      ),
      /** 2:500 */
      startFlow: withReport(({ referralInfo, referralUrl }: StartFlowParams = {}) => {
        return topFunnelStartFlow({
          ...common,
          referralInfo,
          refferalUrl: referralUrl,
        })
      }),
      /** 2:501 */
      screenView: withReport(({ initialData, questionName }: ScreenViewParams = {}) => {
        return mainFunnelIntroScreenView({
          ...common,
          question_name: questionName || common.question_name,
          screenResolution: getScreenResolution(),
          initial_data: initialData ? stringifyAnswer(initialData) : undefined,
        })
      }),
      /** 2:512 */
      typing: withReport(({ text, returnedResults }: TypingParams) => {
        return mainFunnelIntroCategoryUserTyping({ ...common, text, returnedResults })
      }),
      /** 2:522 */
      clickAnswer: withReport(({ questionKey, buttonName, suggestedOption, position }: ClickAnswerParams) => {
        return mainFunnelIntroClickAnAnswer({
          ...common,
          question_name: questionKey,
          button_name: buttonName,
          suggested_option: suggestedOption,
          position,
        })
      }),
      /** 2:527 */
      answer: withReport(({ answer, position, error, payload, questionName, additionalInfo }: AnswerParams) => {
        return mainFunnelIntroMultipleAnswers({
          ...common,
          question_name: questionName || common.question_name,
          payload: JSON.stringify(payload),
          answer: stringifyAnswer(answer),
          additional_info: additionalInfo,
          position,
          error,
        })
      }),
      /** 2:542 */
      generalAction: withReport(({ action, questionKey, error }: GeneralActionParams) => {
        return mainFunnelIntroGeneralAction({ ...common, question_name: questionKey, action, error })
      }),
      /** 2:542 with action: skip */
      skip: withReport(({ payload }: SkipParams = {}) => {
        return mainFunnelIntroGeneralAction({
          ...common,
          action: 'skip',
          ...(payload ? { payload: JSON.stringify(payload) } : {}),
        })
      }),
      /** 2:542 with action: back */
      back: withReport(({ targetBiSlug, payload, questionName }: BackParams = {}) => {
        const [targetScreen] = targetBiSlug ? parsePageBiSlug(targetBiSlug) : [undefined]
        return mainFunnelIntroGeneralAction({
          ...common,
          question_name: questionName || common.question_name,
          action: 'back',
          targetScreen,
          ...(payload ? { payload: JSON.stringify(payload) } : {}),
        })
      }),
      appsSelected: withReport(({ app_id, built_in_app_id }: AppsSelectedParams) => {
        return topFunnelAppsSelected({
          ...common,
          app_id,
          built_in_app_id,
        })
      }),
    }
  }

  return {
    createPageLogger,
    /** 2:599 */
    finishFlow: withReport(({ targetUrl, screenName, screenVersion }: FinishFlowParams) => {
      return topFunnelFinishFlow({
        target_url: targetUrl,
        screenName,
        screenVersion,
        flow: flowBiSlug,
      })
    }),
  }
}

export type FlowLogger = ReturnType<typeof createFlowLogger>

export type PageLogger = ReturnType<FlowLogger['createPageLogger']>

export const parsePageBiSlug = (pageBiSlug: string | undefined) => {
  return (pageBiSlug || 'unknown;unknown').split(';')
}

const stringifyAnswer = (answer: string | object): string => {
  if (typeof answer === 'string') {
    return answer
  }
  return JSON.stringify(answer)
}

const getScreenResolution = () => {
  const { innerWidth, innerHeight } = window
  return `${innerWidth}x${innerHeight}`
}
